import { Box, Paper, Typography } from "@mui/material";
import { Component, type ErrorInfo, type ReactNode } from "react";
import ResponsiveButton from "components/ResponsiveButton";

/**
 * The last line of defence against a white screen.
 *
 * ## The failure this exists for
 *
 * `Routes.tsx` lazy-loads nearly every page, and webpack names each chunk after the build that
 * produced it. An Azure slot swap deploys with `RemoveAdditionalFilesFlag: true`, so the previous
 * build's chunk files are *deleted* from the server. A tab that was open across the swap is still
 * running the old bundle: the moment the user navigates to a page whose chunk they had not already
 * downloaded, the browser asks for a filename that no longer exists, the request 404s, and the
 * `lazy()` promise rejects during render.
 *
 * With no boundary, that error unmounts the entire React tree - the user gets a blank page, not a
 * broken panel. Hence this component, and hence it wraps the whole router.
 *
 * ## Why a reload actually fixes it
 *
 * `index.html` is served `no-store` (see `Program.cs`), so a reload always fetches fresh HTML
 * referencing the *new* chunk filenames. The app is genuinely self-healing; the user simply has no
 * way to know that a refresh is the cure. So for a chunk failure we do it for them: the page is
 * already dead, and a silent reload is strictly better than a blank screen.
 *
 * ## This is the safety net, not the graceful path
 *
 * `AppUpdateBanner` offers a polite "new version available" refresh, but it is driven by a SignalR
 * reconnect - a user with no realtime subscription (no `Alerts:Read` / `Bookings:Read`, or no
 * active account) never opens that connection and never sees the prompt. This boundary is their
 * only protection, so it deliberately shares no code and no state with the version mechanism.
 *
 * ## Only chunk failures reload
 *
 * Any other error renders the fallback panel instead. Auto-reloading on a genuine render bug would
 * turn a visible, reportable crash into an invisible refresh loop.
 */

/** How long a recorded reload attempt suppresses another one. */
const RELOAD_WINDOW_MS = 10_000;

/** sessionStorage key holding the timestamp of the last automatic reload. */
const RELOAD_MARKER_KEY = "cv2.chunkErrorReloadAt";

/**
 * Reloads the page onto the current build.
 *
 * It lives here as a standalone export purely as a test seam: jsdom's `window.location` and
 * `window.location.reload` are non-configurable, so an inline call cannot be asserted on.
 *
 * Deliberately *not* `reloadForAppUpdate` from `code/appVersion`: that is the user-initiated
 * version-prompt path. Two different reasons to reload, kept independent so neither can break the
 * other.
 */
export function reloadForChunkError(): void {
	window.location.reload();
}

/**
 * True when the error is a failed dynamic import rather than a bug in our own render.
 *
 * Browsers disagree on the wording, so match the webpack 5 error `name` *or* any of the phrasings
 * seen in the wild:
 *  - webpack 5: `ChunkLoadError` / "Loading chunk 42 failed"
 *  - Chrome/Edge native ESM: "Failed to fetch dynamically imported module"
 *  - Firefox: "error loading dynamically imported module"
 *  - Safari: "Importing a module script failed"
 */
function isChunkLoadError(error: unknown): boolean {
	if (!(error instanceof Error)) {
		return false;
	}

	if (error.name === "ChunkLoadError") {
		return true;
	}

	const message = error.message ?? "";

	return (
		/loading chunk \S+ failed/i.test(message) ||
		/failed to fetch dynamically imported module/i.test(message) ||
		/error loading dynamically imported module/i.test(message) ||
		/importing a module script failed/i.test(message)
	);
}

/**
 * Records this reload attempt and reports whether it is safe to go ahead.
 *
 * The guard is mandatory: if the chunk is *permanently* missing (a bad deploy, not a stale tab),
 * an unguarded reload is an infinite refresh loop that makes the app completely unusable.
 *
 * Every `sessionStorage` access is wrapped - it throws outright in some privacy modes, and a
 * storage failure must not itself break the boundary. When storage is unavailable we choose *not*
 * to reload: we would have no way to remember that we already tried, so reloading would risk
 * exactly the loop this guard exists to prevent. Those users see the fallback panel and press
 * Reload themselves, which is a worse experience than the silent fix but never a trap.
 */
function shouldReloadNow(): boolean {
	let previousAttempt: string | null;

	try {
		previousAttempt = window.sessionStorage.getItem(RELOAD_MARKER_KEY);
	} catch {
		return false;
	}

	const previousTimestamp = previousAttempt === null ? Number.NaN : Number(previousAttempt);

	if (
		Number.isFinite(previousTimestamp) &&
		Date.now() - previousTimestamp >= 0 &&
		Date.now() - previousTimestamp < RELOAD_WINDOW_MS
	) {
		return false;
	}

	try {
		window.sessionStorage.setItem(RELOAD_MARKER_KEY, String(Date.now()));
	} catch {
		return false;
	}

	return true;
}

interface ErrorBoundaryProps {
	children: ReactNode;
	/**
	 * How the boundary reloads the page. Defaults to `reloadForChunkError` and is only ever passed
	 * in by tests - see that function's note on why an inline `window.location.reload()` cannot be
	 * asserted on under jsdom.
	 */
	reload?: () => void;
}

interface ErrorBoundaryState {
	/** Set in the render phase: something below us threw and the children cannot be shown. */
	hasError: boolean;
	/**
	 * Set in the commit phase, once `componentDidCatch` has decided *not* to reload. It is a
	 * separate flag from `hasError` because those two facts are known at different times - see the
	 * note on `render`.
	 */
	showFallback: boolean;
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
	/**
	 * Deliberately sets nothing but `hasError`, and deliberately stays pure.
	 *
	 * This runs in the render phase, which React may repeat - StrictMode double-invokes it, and a
	 * concurrent render can be thrown away and retried. `shouldReloadNow()` *writes* the
	 * sessionStorage marker, so deciding the reload here would stamp the marker more than once and
	 * the guard would trip against its own write, turning the self-heal off. The decision therefore
	 * belongs in `componentDidCatch`, which runs once, in the commit phase.
	 */
	public static getDerivedStateFromError(): Pick<ErrorBoundaryState, "hasError"> {
		return { hasError: true };
	}

	public state: ErrorBoundaryState = { hasError: false, showFallback: false };

	public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		// Keep it diagnosable: this is the only trace left of a crash that the user never sees.
		console.error("Unhandled error rendering the app", error, errorInfo.componentStack);

		if (isChunkLoadError(error) && shouldReloadNow()) {
			// Leave `showFallback` false. The reload is already under way and a blank frame is the
			// honest thing to show for a page that is about to fix itself.
			this.reload();
			return;
		}

		this.setState({ showFallback: true });
	}

	private reload(): void {
		(this.props.reload ?? reloadForChunkError)();
	}

	private readonly handleReloadClick = (): void => {
		this.reload();
	};

	/**
	 * Renders nothing between catching the error and learning whether we are reloading.
	 *
	 * React commits the fallback to the DOM *before* `componentDidCatch` runs, so a boundary that
	 * rendered the panel on `hasError` alone would already have put "Something went wrong" on
	 * screen by the time we call `window.location.reload()`. Today that is invisible only because
	 * the reload happens synchronously in the same task - one slow frame and the user is told the
	 * page is broken, immediately before it silently repairs itself.
	 *
	 * So the panel waits for `showFallback`, which is only ever set once we have decided *not* to
	 * reload. The reload path renders `null` and stays blank.
	 */
	public render(): ReactNode {
		if (!this.state.hasError) {
			return this.props.children;
		}

		if (!this.state.showFallback) {
			return null;
		}

		return (
			<Box
				role="alert"
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					minHeight: "100vh",
					p: 2
				}}
			>
				<Paper
					elevation={3}
					sx={{
						width: "100%",
						maxWidth: { xs: "100%", sm: "480px" },
						p: { xs: 2, sm: 3 },
						textAlign: "center"
					}}
				>
					<Typography variant="h3" component="h1" fontWeight={700} gutterBottom>
						Something went wrong
					</Typography>
					<Typography variant="text14" sx={{ color: "text.secondary", display: "block", mb: 3 }}>
						This page could not be displayed. Please refresh the page to try again.
					</Typography>
					<ResponsiveButton
						variant="contained"
						color="primary"
						onClick={this.handleReloadClick}
						sx={{
							textTransform: "none",
							fontWeight: 700,
							boxShadow: "none",
							borderRadius: 1
						}}
					>
						Refresh
					</ResponsiveButton>
				</Paper>
			</Box>
		);
	}
}
